/*
 * Pure helpers for the WHG Docs panel inside Word. No Office.js here, so
 * everything is unit-testable.
 *
 * Formstack tag grammar (what the merge engine and the e-sign stamper read):
 *   field        {$Client Full Name}
 *   conditional  {if $Marital_Status == "Married"} … {elseif …} … {else} … {/if}
 *   signature    [sig|req|signer1]  [date|req|signer1]  [initial|req|signer2]  [fullname|req|signer1]
 */

export const FIELD_TAG_RE = /\{\$[^{}]+\}/g;
export const CONDITIONAL_TAG_RE = /\{\/?(?:if|elseif|else)\b[^{}]*\}/gi;
export const SIGNATURE_TAG_RE = /\[(?:sig|date|initial|text|check|fullname)\|(?:req|noreq)\|signer\d+(?:\|[^\]|]*)?\]/gi;

export const TAG_KIND = {
  field: { label: "Field", highlightKey: "fields" },
  signature: { label: "Signature", highlightKey: "signatures" },
  conditional: { label: "Condition", highlightKey: "conditionals" },
};

export const classifyTag = (text) => {
  const t = (text || "").trim();
  if (/^\{\$/.test(t)) return "field";
  if (/^\[(sig|date|initial|text|check|fullname)\|/i.test(t)) return "signature";
  if (/^\{\/?(if|elseif|else)\b/i.test(t)) return "conditional";
  return null;
};

// Every tag string found in a block of text, in order, duplicates kept.
export const findTags = (text) => {
  const found = [];
  const source = text || "";
  for (const re of [FIELD_TAG_RE, CONDITIONAL_TAG_RE, SIGNATURE_TAG_RE]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(source)) !== null) found.push({ text: m[0], index: m.index, kind: classifyTag(m[0]) });
  }
  return found.sort((a, b) => a.index - b.index);
};

export const summarizeTags = (tags) => {
  const counts = { field: 0, signature: 0, conditional: 0 };
  const signers = new Set();
  (tags || []).forEach((t) => {
    if (t.kind) counts[t.kind] += 1;
    if (t.kind === "signature") {
      const m = /signer(\d+)/i.exec(t.text);
      if (m) signers.add(Number(m[1]));
    }
  });
  return { ...counts, signers: [...signers].sort((a, b) => a - b) };
};

export const fieldTag = (name) => `{$${(name || "").trim()}}`;

// Formstack: letters, digits, spaces and underscores; must not start with a digit.
export const cleanFieldName = (raw) => {
  const cleaned = (raw || "")
    .replace(/[{}$]/g, "")
    .replace(/[^A-Za-z0-9 _]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return /^\d/.test(cleaned) ? `Field ${cleaned}` : cleaned;
};

export const signatureTag = (type, signer, { required = true } = {}) =>
  `[${type}|${required ? "req" : "noreq"}|signer${signer}]`;

export const filenameFromUrl = (url) => {
  if (!url) return "";
  const noQuery = String(url).split("?")[0].split("#")[0];
  const last = noQuery.split(/[\\/]/).pop() || "";
  try {
    return decodeURIComponent(last);
  } catch (e) {
    return last;
  }
};

export const formstackIdFromFilename = (filename) => {
  const m = /\[([^[\]]+)\]\.docx$/i.exec((filename || "").trim());
  return m ? m[1].trim() : "";
};

export const filterFields = (fields, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return fields || [];
  return (fields || []).filter(
    (f) =>
      (f.field_name || "").toLowerCase().includes(q) ||
      (f.field_label || "").toLowerCase().includes(q) ||
      (f.sf_field_name || "").toLowerCase().includes(q)
  );
};

/*
 * "Review my brief": what the server's review looks like once it is in the pane.
 *   notes — high-level, each anchored to a paragraph; become margin comments
 *   edits — original → replacement inside one paragraph; become tracked changes
 */

export const EDIT_KINDS = {
  words: "Word choice",
  passive: "Passive voice",
  style: "House style",
  clarity: "Clarity",
};

export const NOTE_KINDS = {
  structure: "Structure",
  voice: "Voice",
  words: "Word choice",
  style: "Style",
};

export const FORUM_LABELS = {
  cavc: "Court of Appeals for Veterans Claims brief",
  board: "Board brief",
  hlr: "Higher-Level Review argument",
  other: "Draft",
};

// Review is the pane's first job when it is on for this person and the open
// file is not a WHG Docs template; template authors still land on Fields.
export const initialTab = (ctx) => (ctx?.brief_review?.enabled && !ctx?.matched ? "review" : "fields");

export const countByKind = (edits) =>
  (edits || []).reduce((counts, edit) => {
    counts[edit.kind] = (counts[edit.kind] || 0) + 1;
    return counts;
  }, {});

export const visibleEdits = (edits, { kind = "all", hidden = new Set() } = {}) =>
  (edits || []).filter((edit) => !hidden.has(edit.id) && (kind === "all" || edit.kind === kind));

// What Word's paragraph collection gives us, trimmed to what the server reads.
// Indexes are positions in that collection and must survive the round trip.
export const paragraphsForReview = (paragraphs) =>
  (paragraphs || []).map((p, position) => ({
    index: typeof p.index === "number" ? p.index : position,
    text: p.text || "",
    style: p.style || "",
    in_table: Boolean(p.in_table),
  }));

export const reviewHeadline = (review) => {
  const stats = review?.stats || {};
  const forum = FORUM_LABELS[review?.forum] || FORUM_LABELS.other;
  const parts = [`${forum} · ${stats.paragraphs || 0} paragraphs`];
  parts.push(`${stats.notes || 0} note${stats.notes === 1 ? "" : "s"}`);
  parts.push(`${stats.edits || 0} line edit${stats.edits === 1 ? "" : "s"}`);
  return parts.join(" · ");
};

// "Note 3" / "Edit 7": the number a suggestion carries in the pane and in
// the document, so the two can be matched by eye. Ids come numbered from
// the server (n1…, e1… in document order).
export const itemLabel = (item, totals = {}) => {
  const id = String(item?.id || "");
  const number = id.slice(1);
  if (id.startsWith("n")) return totals.notes ? `Note ${number} of ${totals.notes}` : `Note ${number}`;
  if (id.startsWith("e")) return totals.edits ? `Edit ${number} of ${totals.edits}` : `Edit ${number}`;
  return "Suggestion";
};

const q = (text) => `\u201c${text}\u201d`;

// The text of the margin comment a note becomes. Prefixed and numbered so
// nobody mistakes it for a colleague's comment — Word shows it under the
// signed-in person's name.
export const commentText = (note, totals = {}) => {
  const where = (note.paragraphs || []).length > 1 ? ` (paragraphs ${note.paragraphs.join(", ")})` : "";
  return `[WHG Docs review — ${itemLabel(note, totals)} · ${NOTE_KINDS[note.kind] || "Note"}] ${note.title}${where}: ${note.note}`;
};

// The comment a line edit becomes when the whole review is marked up.
export const editCommentText = (edit, totals = {}) => {
  const why = edit.why ? ` — ${edit.why}` : "";
  return `[WHG Docs review — ${itemLabel(edit, totals)} · ${EDIT_KINDS[edit.kind] || "Edit"}] Replace ${q(edit.original)} with ${q(edit.replacement)}${why}`;
};

// A short pointer on each other paragraph a note involves, so "argued twice,
// here and again later" is marked at both places.
export const pointerText = (note, totals = {}) =>
  `[WHG Docs review — ${itemLabel(note, totals)} · ${NOTE_KINDS[note.kind] || "Note"}] ↳ Also part of this note (its comment is on paragraph ${note.anchor}): ${note.title}`;

const MAX_POINTERS = 4;

// Everything "Mark all in the document" places, in order: each note on its
// words (plus pointers), then each line edit on the phrase it changes.
// Dismissed suggestions and edits already applied are left out. Notes that
// were commented one at a time stay in: marking starts by removing every
// comment of ours, so they must be put back. A mark's `id` names the
// suggestion it pins; pointers carry none, so only the pinning comment
// counts as the note being in the document.
export const marksFor = (review, { hidden = new Set(), done = {} } = {}) => {
  const totals = { notes: (review?.notes || []).length, edits: (review?.edits || []).length };
  const marks = [];
  for (const note of review?.notes || []) {
    if (hidden.has(note.id)) continue;
    const label = itemLabel(note, totals);
    marks.push({ id: note.id, target: "note", label, paragraph: note.anchor, quote: note.quote || "", text: commentText(note, totals) });
    (note.paragraphs || [])
      .filter((p) => p !== note.anchor)
      .slice(0, MAX_POINTERS)
      .forEach((p) => marks.push({ target: "note", label, paragraph: p, quote: "", text: pointerText(note, totals) }));
  }
  for (const edit of review?.edits || []) {
    if (hidden.has(edit.id) || done[edit.id]) continue;
    marks.push({
      id: edit.id,
      target: "edit",
      label: itemLabel(edit, totals),
      paragraph: edit.paragraph,
      original: edit.original,
      occurrence: edit.occurrence,
      text: editCommentText(edit, totals),
    });
  }
  return marks;
};

// The reviewer names places as "[28]" or "[36]-[38]"; split a sentence into
// plain text and paragraph references so the pane can make each one a link.
export const paragraphRefs = (text) => {
  const out = [];
  const pattern = /\[(\d{1,4})\]/g;
  let last = 0;
  let match;
  const source = String(text || "");
  while ((match = pattern.exec(source)) !== null) {
    if (match.index > last) out.push({ text: source.slice(last, match.index) });
    out.push({ ref: Number(match[1]) });
    last = match.index + match[0].length;
  }
  if (last < source.length) out.push({ text: source.slice(last) });
  return out;
};
